import { Col, ControlLabel, Glyphicon, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FormGroupContainer } from 'components/forms/genericFormField/GenericFormField.styles';
import { formatOptionLabelWithHighlight } from 'components/forms/multiSelectField/formatOptionLabel';
import MultiSelectField from 'components/forms/multiSelectField/MultiSelectField';
import { BaseOption } from 'components/forms/multiSelectField/MultiSelectField.types';
import TextAreaField from 'components/forms/textAreaField/TextAreaField';
import TextField from 'components/forms/textField/TextField';
import Tooltip from 'components/tooltip/Tooltip';
import {
  SettingsColumnStyled,
  SettingsSectionStyled,
} from '../../DirectBookingEngine.styles';
import HeroImageField from './HeroImageField';
import SocialIconsField from './SocialIconsField';
import { HeroFieldRowStyled } from './HeroImage.styles';
import BrandColorsField from './colorSettings/BrandColorsField';
import PageNavigationField from './navigationSettings/PageNavigationField';

const WebsiteSettingsSection = ({
  heroUrl,
  propertyOptions,
}: {
  heroUrl?: string | null;
  propertyOptions: BaseOption[];
}) => {
  const { t } = useTranslation();

  const fieldProps = {
    colSmInput: 7,
    colSmLabel: 5,
  };

  return (
    <SettingsSectionStyled
      data-testid="website-settings-section"
      defaultExpanded
      label={t('pageDirectBookingEngine.websiteSettings.sectionTitle')}
      variant="card"
    >
      <Row>
        <SettingsColumnStyled>
          <FormGroupContainer $name="heroImage">
            <Col
              componentClass={ControlLabel}
              data-testid="heroImage-field-label"
              sm={5}
              xs={4}
            >
              {t('pageDirectBookingEngine.websiteSettings.heroImage')}
            </Col>
            <Col sm={7} xs={8}>
              <HeroFieldRowStyled>
                <HeroImageField heroUrl={heroUrl} />
                <Tooltip
                  id="heroImage-tooltip"
                  text={t(
                    'pageDirectBookingEngine.websiteSettings.heroImageTooltip',
                  )}
                >
                  <Glyphicon
                    data-testid="heroImage-tooltip-icon"
                    glyph="info-sign"
                  />
                </Tooltip>
              </HeroFieldRowStyled>
            </Col>
          </FormGroupContainer>
          <TextField
            {...fieldProps}
            label={t('pageDirectBookingEngine.websiteSettings.headline')}
            name="headline"
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.headlinePlaceholder',
            )}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.headlineTooltip',
            )}
          />
          <TextField
            {...fieldProps}
            label={t('pageDirectBookingEngine.websiteSettings.subHeadline')}
            name="subHeadline"
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.subHeadlinePlaceholder',
            )}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.subHeadlineTooltip',
            )}
          />
          <TextField
            {...fieldProps}
            label={t('pageDirectBookingEngine.websiteSettings.welcomeTitle')}
            name="welcomeTitle"
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.welcomeTitlePlaceholder',
            )}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.welcomeTitleTooltip',
            )}
          />
          <TextAreaField
            {...fieldProps}
            label={t('pageDirectBookingEngine.websiteSettings.welcomeContent')}
            name="welcomeContent"
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.welcomeContentPlaceholder',
            )}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.welcomeContentTooltip',
            )}
            rows={6}
          />
        </SettingsColumnStyled>
        <SettingsColumnStyled>
          <BrandColorsField />
          <PageNavigationField />
          <MultiSelectField
            {...fieldProps}
            containerClassName="featured-properties-field"
            formatOptionLabel={formatOptionLabelWithHighlight}
            label={t(
              'pageDirectBookingEngine.websiteSettings.featuredProperties',
            )}
            name="featuredProperties"
            options={propertyOptions}
            placeholder={t('common.search')}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.featuredPropertiesTooltip',
            )}
          />
          <SocialIconsField />
          <TextField
            {...fieldProps}
            label={t('pageDirectBookingEngine.websiteSettings.widgetCtaTitle')}
            name="widgetCtaTitle"
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.widgetCtaTitlePlaceholder',
            )}
            rightTooltip={t(
              'pageDirectBookingEngine.websiteSettings.widgetCtaTitleTooltip',
            )}
          />
        </SettingsColumnStyled>
      </Row>
    </SettingsSectionStyled>
  );
};

WebsiteSettingsSection.defaultProps = {
  heroUrl: undefined,
};

export default WebsiteSettingsSection;
